import { lookupsJson, paymentType as PaymentType } from '@ourbranch/lookups';

/**
 * #36434: the farm payment-plan facts staff-fe's offer "Price details" panel shows (Payment Frequency,
 * Payment Method, the View Schedule rows) and the CFM/Forreston farm Quote/Application print. Both
 * read the same rater output — the option's `farmSchedule` (`billingHandler.payments.getOfferPayment`
 * in rater-v2's offer-rater-manager) — through these helpers, so the document can't drift from the UI.
 */

/**
 * True for the plans the UI treats as installments ("Next N payments of $X" + View Schedule): Monthly,
 * 4 payments, 2 payments; false for One Time, Mortgage, or unset. The `paymentType` codes are read at
 * call time, not module load, so importing farm-utils never touches `@ourbranch/lookups` eagerly (staff-fe
 * suites mock that module partially).
 */
export const isFarmInstallmentPlan = (farmPaymentType?: string | null): boolean =>
  !!farmPaymentType && [PaymentType.Monthly, PaymentType.QuarterTerm, PaymentType.HalfTerm].includes(farmPaymentType);

const lookupLabel = (options: { id: string; value: string }[] | undefined, id?: string | null): string =>
  (id && options?.find((option) => option.id === id)?.value) || '';

/** The UI's Payment Frequency label (`farmPaymentType` lookup: Monthly, 4 payments, One Time, …). */
export const farmPaymentFrequencyLabel = (farmPaymentType?: string | null): string =>
  lookupLabel(lookupsJson.farmPaymentType, farmPaymentType);

/** The UI's Payment Method label (`farmPaymentMethod` lookup: Credit Card, Bank Withdrawal, …). */
export const farmPaymentMethodLabel = (farmPaymentMethod?: string | null): string =>
  lookupLabel(lookupsJson.farmPaymentMethod, farmPaymentMethod);

/** One `farmSchedule` entry as it rides on the offer option (every field nullable). */
export interface FarmSchedulePayment {
  date?: string | null;
  remainingMonthly?: number | null;
  transactionFee?: number | null;
  total?: number | null;
}

/** The billed amount of a schedule row: `total ?? remainingMonthly` (the View Schedule convention). */
export const farmSchedulePaymentAmount = (payment: FarmSchedulePayment | null | undefined): number =>
  payment?.total ?? payment?.remainingMonthly ?? 0;

export interface FarmPaymentScheduleRow {
  /** `YYYY-MM-DD`, as stored. */
  date: string;
  amount: number;
  /** The first row: the down payment / amount due today. */
  isDownPayment: boolean;
}

/**
 * The rows the UI's View Schedule lists, in order — the first is the down payment and the last carries
 * billing's rounding true-up, both exactly as stored. Empty for a One Time or Mortgage plan (the UI
 * shows no schedule for those) and for an option the rater never scheduled. Undated rows are dropped,
 * as the View Schedule tooltip does.
 */
export function getFarmPaymentScheduleRows({
  farmPaymentType,
  farmSchedule
}: {
  farmPaymentType?: string | null;
  farmSchedule?: (FarmSchedulePayment | null | undefined)[] | null;
}): FarmPaymentScheduleRow[] {
  if (!isFarmInstallmentPlan(farmPaymentType)) {
    return [];
  }
  return (farmSchedule ?? [])
    .filter((payment): payment is FarmSchedulePayment => !!payment?.date)
    .map((payment, index) => ({
      date: payment.date as string,
      amount: farmSchedulePaymentAmount(payment),
      isDownPayment: index === 0
    }));
}
